import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { isValidCPF } from './utils/cpfUtils';
import { isValidCNPJ } from './utils/cnpjUtils';
import { API_BASE_URL } from '../config/ApiConfig';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import FavorecidoModal from './components/modals/FavorecidoModal';
import usePagination10 from './components/hooks/usePagination10';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = {
  idEscola: '', cpfOrCnpj: '', nome: '', tipo: '', email: '', telefone: '', celular: '',
  logrNome: '', logrNum: '', logrCompl: '', logrBairro: '', logrCep: '', logrCidade: '', logrUf: ''
};

function Favorecidos() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const [items, setItems] = useState([]);
  const [ufs, setUfs] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '' });
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBuscandoCnpj, setIsBuscandoCnpj] = useState(false);
  const [cidadesKey, setCidadesKey] = useState(0);
  const [search, setSearch] = useState('');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const sortedItems = items.slice().sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  const filteredItems = search ? sortedItems.filter(i => i.nome.toLowerCase().includes(search.toLowerCase())) : sortedItems;
  const { setPagination } = usePagination10(filteredItems.length);
  const displayed = filteredItems.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  useEffect(() => { setPagination.onFirstItems(); }, [search]); // eslint-disable-line

  useEffect(() => {
    axios.get(`${API_BASE_URL.UF}/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUfs(r.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (form.logrUf) {
      axios.get(`${API_BASE_URL.MUNICIPIO}/uf/${form.logrUf}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          setCidades(r.data);
          if (form.logrCidade) {
            const norm = s => s ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase() : '';
            const match = r.data.find(c => norm(c.nome) === norm(form.logrCidade));
            if (match && match.nome !== form.logrCidade) {
              setForm(f => ({ ...f, logrCidade: match.nome }));
            }
          }
        }).catch(() => setCidades([]));
    } else {
      setCidades([]);
    }
  }, [form.logrUf, showModal, cidadesKey, token]);

  useEffect(() => {
    if (!cnpj) { showDefaultModal('error', 'Escola não selecionada.'); setIsLoading(false); return; }
    setIsLoading(true);
    axios.get(`${API_BASE_URL.FAVORECIDO}/findByEscola`, {
      params: { idEscola: cnpj }, headers: { Authorization: `Bearer ${token}` }
    }).then(r => setItems(r.data))
      .catch(() => showDefaultModal('error', 'Erro ao carregar favorecidos.'))
      .finally(() => setIsLoading(false));
  }, [token, cnpj, atualizar]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => {
      const updated = { ...f, [name]: value };
      if (name === 'logrUf') updated.logrCidade = '';
      return updated;
    });
  }
  async function handleBuscarCnpj() {
    const cnpjNum = stripMask(form.cpfOrCnpj);
    if (cnpjNum.length !== 14) { showDefaultModal('error', 'Digite um CNPJ válido antes de buscar.'); return; }
    setIsBuscandoCnpj(true);
    try {
      const r = await axios.get(`${API_BASE_URL.CNPJ_PROXY}/${cnpjNum}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = r.data;
      if (d.status === 'ERROR') { showDefaultModal('error', d.message || 'CNPJ não encontrado.'); return; }
      setForm(f => ({
        ...f,
        nome: d.nome || f.nome,
        email: d.email || f.email,
        telefone: d.telefone || f.telefone,
        logrNome: d.logradouro || f.logrNome,
        logrNum: d.numero || f.logrNum,
        logrCompl: d.complemento || f.logrCompl,
        logrBairro: d.bairro || f.logrBairro,
        logrCep: (d.cep || '').replace(/\D/g, '') || f.logrCep,
        logrUf: d.uf || f.logrUf,
        logrCidade: d.municipio || f.logrCidade,
      }));
      setCidadesKey(k => k + 1);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        showDefaultModal('error', 'Limite de consultas atingido. Aguarde 1 minuto e tente novamente.');
      } else if (status === 400) {
        showDefaultModal('error', 'CNPJ inválido.');
      } else {
        showDefaultModal('error', `Erro ao consultar CNPJ${status ? ` (${status})` : ''}. Tente novamente em instantes.`);
      }
    } finally {
      setIsBuscandoCnpj(false);
    }
  }

  function handleNew() { setIsEditing(false); setForm({ ...emptyForm, idEscola: cnpj || '' }); setShowModal(true); }
  function handleEdit() {
    if (selected.length !== 1) return;
    setIsEditing(true);
    setForm({ ...selected[0] });
    setShowModal(true);
  }

  function stripMask(value) { return value ? value.replace(/\D/g, '') : ''; }

  function handleSave(formLimpo) {
    const raw = stripMask(formLimpo.cpfOrCnpj);
    if (formLimpo.tipo === 'PF' && !isValidCPF(raw)) {
      showDefaultModal('error', 'CPF inválido.');
      return;
    }
    if (formLimpo.tipo === 'PJ' && !isValidCNPJ(raw)) {
      showDefaultModal('error', 'CNPJ inválido.');
      return;
    }
    const payload = {
      ...formLimpo,
      cpfOrCnpj: raw,
      telefone: stripMask(formLimpo.telefone),
      celular: stripMask(formLimpo.celular),
      logrCep: stripMask(formLimpo.logrCep),
    };
    axios.post(`${API_BASE_URL.FAVORECIDO}/save`, payload, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => { setShowModal(false); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao salvar favorecido.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s => axios.delete(`${API_BASE_URL.FAVORECIDO}/delete`, {
      params: { idEscola: s.idEscola, cpfOrCnpj: s.cpfOrCnpj }, headers: { Authorization: `Bearer ${token}` }
    }))).then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir favorecido.'))
    );
  }

  function toggleSelect(item) {
    const key = `${item.idEscola}_${item.cpfOrCnpj}`;
    setSelected(prev => prev.find(s => `${s.idEscola}_${s.cpfOrCnpj}` === key) ? [] : [item]);
  }

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="FAVORECIDOS" icon="bi-person-check" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
            <div className="input-group input-group-sm toolbar-search">
              <span className="input-group-text bg-light"><i className="bi bi-search"></i></span>
              <input
                type="text"
                className="form-control"
                placeholder="buscar por nome..."
                value={search}
                onChange={e => setSearch(e.target.value.toUpperCase())}
                style={{ textTransform: 'uppercase', fontVariant: 'normal' }}
              />
            </div>
          </Toolbar>
          <TableContainer isLoading={false}>
                              <DataTable head={<tr>
                  <th style={{ width: '3rem' }}>#</th><th>CPF/CNPJ</th><th>Nome</th><th>Tipo</th><th>Email</th><th>Telefone</th>
                </tr>}>
                <tbody>
                  {displayed.map((item, idx) => {
                    const key = `${item.idEscola}_${item.cpfOrCnpj}`;
                    return (
                      <tr key={key} className={selected.find(s => `${s.idEscola}_${s.cpfOrCnpj}` === key) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                        <td>{setPagination.startIndex + idx + 1}</td><td>{item.cpfOrCnpj}</td><td>{item.nome}</td><td>{item.tipo}</td><td>{item.email}</td><td>{item.telefone}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <FavorecidoModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} ufs={ufs} cidades={cidades} onBuscarCnpj={handleBuscarCnpj} isBuscandoCnpj={isBuscandoCnpj} isEditing={isEditing} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Favorecidos;
